import {
  XAxis,
  YAxis,
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { demoTwrData, demoTwrSummary } from "@/lib/landing-demo-data";
import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";

const PERIODS = ["1D", "1W", "1M", "1Y", "MAX"] as const;
type Period = (typeof PERIODS)[number];

const twrChartConfig = {
  twr: {
    label: "Portfolio",
    color: "hsl(var(--chart-2, var(--primary)))",
  },
  benchmark: {
    label: "S&P 500",
    color: "hsl(25, 95%, 53%)",
  },
} satisfies ChartConfig;

export default function PerformanceShowcase() {
  const [period, setPeriod] = useState<Period>("1Y");

  // Filter data based on selected period (include one extra point for rebasing)
  const chartData = useMemo(() => {
    const pointsByPeriod: Record<Period, number> = {
      "1D": 2,
      "1W": 8,
      "1M": 31,
      "1Y": 366,
      "MAX": demoTwrData.length,
    };
    const pointCount = pointsByPeriod[period];
    const sliced = demoTwrData.slice(-pointCount);
    if (sliced.length === 0) return [];

    const basePortfolio = sliced[0].twr;
    const baseBenchmark = sliced[0].benchmark;
    const basePortfolioFactor = 1 + basePortfolio / 100;
    const baseBenchmarkFactor = 1 + baseBenchmark / 100;

    return sliced.map((point) => {
      const portfolio = basePortfolioFactor > 0
        ? ((1 + point.twr / 100) / basePortfolioFactor - 1) * 100
        : point.twr - basePortfolio;
      const benchmark = baseBenchmarkFactor > 0
        ? ((1 + point.benchmark / 100) / baseBenchmarkFactor - 1) * 100
        : point.benchmark - baseBenchmark;

      return {
        ...point,
        twr: portfolio,
        benchmark,
      };
    });
  }, [period]);

  // Calculate Y-axis domain
  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 0] as [number, number];
    const allValues = chartData.flatMap((d) => [d.twr, d.benchmark]);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1;
    return [Math.floor(min - padding), Math.ceil(max + padding)] as [number, number];
  }, [chartData]);

  // Format X-axis labels
  const formatXAxis = (timestamp: string) => {
    const date = new Date(timestamp);
    if (period === "1D" || period === "1W" || period === "1M") {
      return date.toLocaleDateString(undefined, { day: "numeric" });
    }
    return date.toLocaleDateString(undefined, { month: "short" });
  };

  const portfolioReturn = chartData[chartData.length - 1]?.twr ?? demoTwrSummary.portfolioReturn;
  const benchmarkReturn = chartData[chartData.length - 1]?.benchmark ?? demoTwrSummary.benchmarkReturn;
  const outperformance = portfolioReturn - benchmarkReturn;

  return (
    <section id="features" className="w-full py-20 bg-muted/30">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest">
            <TrendingUp className="w-3 h-3" />
            Performance Tracking
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-serif">
            Compare Your Returns to{" "}
            <span className="text-primary">Market Benchmarks</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Track your Time-Weighted Return (TWR) and see how your portfolio stacks up 
            against the S&P 500 and other benchmarks.
          </p>
        </div>

        {/* Chart Card */}
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-border/50 bg-card p-6 md:p-8 shadow-xl">
            {/* Chart Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-bold tracking-tight">
                  Time-Weighted Return
                </h3>
                <p className="text-sm text-muted-foreground">
                  Growth relative to cash flow events
                </p>
              </div>
              
              {/* Period Selector */}
              <div className="flex items-center gap-1 bg-secondary/50 rounded-full p-1 border border-border/40">
                {PERIODS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={cn(
                      "px-4 py-1.5 text-xs font-semibold rounded-full transition-all",
                      period === p
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Performance Cards */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="bg-primary/5 rounded-xl p-2 px-4 border border-primary/10">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-bold text-primary uppercase tracking-widest">
                    Portfolio
                  </span>
                  <span className={cn(
                    "text-xl font-bold tracking-tight",
                    portfolioReturn >= 0 ? "text-primary" : "text-red-500"
                  )}>
                    {portfolioReturn >= 0 ? "+" : ""}{portfolioReturn.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="bg-orange-500/5 rounded-xl p-2 px-4 border border-orange-500/10">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">
                    {demoTwrSummary.benchmarkSymbol}
                  </span>
                  <span className={cn(
                    "text-xl font-bold tracking-tight",
                    benchmarkReturn >= 0 ? "text-orange-500" : "text-red-500"
                  )}>
                    {benchmarkReturn >= 0 ? "+" : ""}{benchmarkReturn.toFixed(1)}%
                  </span>
                </div>
              </div>

              {outperformance > 0 && (
                <div className="ml-auto bg-emerald-500/10 rounded-xl p-2 px-4 border border-emerald-500/20">
                  <span className="text-sm font-semibold text-emerald-600">
                    Outperforming by +{outperformance.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>

            {/* Chart */}
            <div className="h-[300px] md:h-[400px]">
              <ChartContainer
                config={twrChartConfig}
                className="h-full w-full"
              >
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, left: 10, right: 20, bottom: 16 }}
                >
                  <defs>
                    <linearGradient id="colorTWRLanding" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="hsl(var(--chart-2))"
                        stopOpacity={0.15}
                      />
                      <stop
                        offset="95%"
                        stopColor="hsl(var(--chart-2))"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.3}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatXAxis}
                    tick={{
                      fontSize: 10,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval="preserveStartEnd"
                    minTickGap={50}
                  />
                  <YAxis
                    domain={yAxisDomain}
                    tickFormatter={(value) => `${value.toFixed(0)}%`}
                    tick={{
                      fontSize: 10,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={45}
                  />
                  <ChartTooltip
                    cursor={{
                      stroke: "hsl(var(--muted-foreground))",
                      strokeWidth: 1,
                      strokeOpacity: 0.2,
                    }}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(value) =>
                          new Date(value).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        }
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="twr"
                    name="Portfolio"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    fill="url(#colorTWRLanding)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="benchmark"
                    name={demoTwrSummary.benchmarkName}
                    stroke="rgb(249, 115, 22)"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                </ComposedChart>
              </ChartContainer>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[hsl(var(--chart-2))]" />
                <span className="text-muted-foreground">Your Portfolio</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-orange-500" style={{ width: 12 }} />
                <span className="text-muted-foreground">{demoTwrSummary.benchmarkName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
